import React, { useState, useEffect } from 'react';
import { MonitorDetail } from '../components/MonitorStatus';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

export default function SiteOverview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [summary, setSummary] = useState(null);
  const [keywords, setKeywords] = useState([]);
  const [googleStatus, setGoogleStatus] = useState(null);
  const [gscData, setGscData] = useState(null);
  const [ga4Summary, setGa4Summary] = useState(null);
  const [checklistSummary, setChecklistSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.getSite(id),
      api.getRankingSummary(id).catch(() => null),
      api.getKeywords(id).catch(() => ({ keywords: [] })),
      api.getGoogleStatus(id).catch(() => ({ connected: false })),
      api.getChecklistSummary(id).catch(() => null),
    ]).then(([s, sum, kw, gs, cl]) => {
      setSite(s);
      setSummary(sum);
      setKeywords(kw.keywords || []);
      setGoogleStatus(gs);
      setChecklistSummary(cl);
      setLoading(false);
      // Load GSC + GA4 if connected
      if (gs.connected) {
        api.getGscTopQueries(id, 30, 10).then(setGscData).catch(() => {});
        api.getGa4Summary(id, 30).then(setGa4Summary).catch(() => {});
      }
    }).catch(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="loading">טוען...</div>;
  if (!site) return <div className="loading">אתר לא נמצא</div>;

  const topKeywords = keywords
    .filter(k => k.latest_desktop)
    .sort((a, b) => a.latest_desktop - b.latest_desktop)
    .slice(0, 10);

  const posColor = (pos) => { if (pos <= 3) return 'var(--green)'; if (pos <= 10) return '#22D3EE'; if (pos <= 20) return 'var(--orange)'; return 'var(--red)'; };
  function formatNum(n) { if (n >= 1000) return (n / 1000).toFixed(1) + 'K'; return n; }
  function changeArrow(val) {
    if (!val || val === 0) return { text: '0%', color: 'var(--text-muted)' };
    if (val > 0) return { text: '+' + val + '%', color: 'var(--green)' };
    return { text: val + '%', color: 'var(--red)' };
  }

  return (
    <>
      <MonitorDetail site={site} />
      {/* Metric cards row */}
      <div className="site-overview-header">
        <div className="site-metric-card animate-in stagger-1">
          <div className="label">ביטויים ב-TOP 3</div>
          <div className="value-row"><div className="value">{summary?.ranked_top3 ?? '-'}</div></div>
        </div>
        <div className="site-metric-card animate-in stagger-2">
          <div className="label">בעמוד ראשון</div>
          <div className="value-row"><div className="value">{summary?.ranked_top10 ?? '-'}</div></div>
        </div>
        <div className="site-metric-card animate-in stagger-3">
          <div className="label">סה"כ ביטויים</div>
          <div className="value-row"><div className="value">{summary?.total_keywords ?? '-'}</div></div>
        </div>
        {checklistSummary && checklistSummary.total > 0 && (
          <div className="site-metric-card animate-in stagger-4" onClick={() => navigate(`/sites/${id}/checklist`)} style={{cursor:'pointer'}}>
            <div className="label">שיפורים פתוחים</div>
            <div className="value-row">
              <div className="value" style={{color: (checklistSummary.by_priority?.critical || 0) > 0 ? 'var(--red)' : 'var(--orange)'}}>
                {checklistSummary.by_status?.pending || 0}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Top Keywords by rank */}
      {topKeywords.length > 0 && (
        <div className="section-card animate-in">
          <div className="section-header">
            <div className="section-title">ביטויים מובילים - מעקב מיקומים</div>
            <div className="section-actions">
              <button className="btn-sm" onClick={() => navigate(`/sites/${id}/keywords`)}>כל הביטויים</button>
              <button className="btn-sm primary" onClick={() => api.triggerRankCheck(id).then(() => window.location.reload())}>בדיקת מיקומים</button>
            </div>
          </div>
          <table className="rankings-table">
            <thead>
              <tr>
                <th>ביטוי</th>
                <th>מיקום</th>
                <th>שינוי יומי</th>
                <th>URL</th>
              </tr>
            </thead>
            <tbody>
              {topKeywords.map(kw => {
                const pos = kw.latest_desktop;
                const posClass = pos <= 3 ? 'top3' : pos <= 10 ? 'top10' : pos <= 20 ? 'top20' : 'beyond';
                const change = kw.change_day;
                const changeDir = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
                const changeText = change > 0 ? `▲ ${change}` : change < 0 ? `▼ ${Math.abs(change)}` : '-';
                return (
                  <tr key={kw.id}>
                    <td><span className="keyword-text">{kw.keyword}</span></td>
                    <td><span className={`position-badge ${posClass}`}>{pos}</span></td>
                    <td><span className={`change-badge ${changeDir}`}>{changeText}</span></td>
                    <td style={{direction:'ltr', textAlign:'right', fontSize:'12px', color:'var(--text-muted)', maxWidth:'180px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{kw.url_ranked || '-'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}


      {/* GSC Top Queries */}
      {gscData && gscData.rows && gscData.rows.length > 0 && (
        <div className="section-card animate-in" style={{marginBottom:'1rem'}}>
          <div className="section-header">
            <div className="section-title">Search Console - ביטויים מובילים</div>
            <button className="btn-sm" onClick={() => navigate(`/sites/${id}/gsc`)}>כל הנתונים</button>
          </div>
          <table className="rankings-table">
            <thead>
              <tr>
                <th>ביטוי</th>
                <th>קליקים</th>
                <th>חשיפות</th>
                <th>CTR</th>
                <th>מיקום</th>
              </tr>
            </thead>
            <tbody>
              {gscData.rows.slice(0, 8).map((row, i) => (
                <tr key={i}>
                  <td><span className="keyword-text">{row.keys[0]}</span></td>
                  <td style={{textAlign:'center', fontFamily:"'JetBrains Mono',monospace", color:'var(--accent)', fontWeight:700}}>{formatNum(row.clicks)}</td>
                  <td style={{textAlign:'center', fontFamily:"'JetBrains Mono',monospace"}}>{formatNum(row.impressions)}</td>
                  <td style={{textAlign:'center', fontFamily:"'JetBrains Mono',monospace"}}>{row.ctr}%</td>
                  <td style={{textAlign:'center', fontFamily:"'JetBrains Mono',monospace", fontWeight:700, color:posColor(row.position)}}>{row.position}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}


      {/* GA4 Summary - if connected */}
      {ga4Summary && (
        <div className="section-card animate-in" style={{marginBottom:'1rem'}}>
          <div className="section-header">
            <div className="section-title">Analytics - 30 יום אחרונים</div>
            <button className="btn-sm" onClick={() => navigate(`/sites/${id}/analytics`)}>פרטים</button>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:'10px', marginTop:'8px'}}>
            <div style={{background:'var(--bg)', borderRadius:'8px', padding:'12px', textAlign:'center', border:'1px solid var(--border)'}}>
              <div style={{fontSize:'22px', fontWeight:700, fontFamily:'var(--mono)', color:'var(--accent)'}}>{formatNum(ga4Summary.sessions)}</div>
              <div style={{fontSize:'11px', color:'var(--text-muted)', marginTop:'2px'}}>סשנים <span style={{color: changeArrow(ga4Summary.sessions_change).color}}>{changeArrow(ga4Summary.sessions_change).text}</span></div>
            </div>
            <div style={{background:'var(--bg)', borderRadius:'8px', padding:'12px', textAlign:'center', border:'1px solid var(--border)'}}>
              <div style={{fontSize:'22px', fontWeight:700, fontFamily:'var(--mono)', color:'#22D3EE'}}>{formatNum(ga4Summary.users)}</div>
              <div style={{fontSize:'11px', color:'var(--text-muted)', marginTop:'2px'}}>משתמשים</div>
            </div>
            <div style={{background:'var(--bg)', borderRadius:'8px', padding:'12px', textAlign:'center', border:'1px solid var(--border)'}}>
              <div style={{fontSize:'22px', fontWeight:700, fontFamily:'var(--mono)', color:'var(--text)'}}>{formatNum(ga4Summary.pageviews)}</div>
              <div style={{fontSize:'11px', color:'var(--text-muted)', marginTop:'2px'}}>צפיות</div>
            </div>
            <div style={{background:'var(--bg)', borderRadius:'8px', padding:'12px', textAlign:'center', border:'1px solid var(--border)'}}>
              <div style={{fontSize:'22px', fontWeight:700, fontFamily:'var(--mono)', color:'var(--orange)'}}>{Math.round(ga4Summary.bounce_rate * 100)}%</div>
              <div style={{fontSize:'11px', color:'var(--text-muted)', marginTop:'2px'}}>Bounce</div>
            </div>
          </div>
        </div>
      )}

      {/* No data state */}
      {!googleStatus?.connected && topKeywords.length === 0 && (
        <div className="section-card animate-in" style={{textAlign:'center', padding:'2rem'}}>
          <p style={{color:'var(--text-muted)', marginBottom:'12px'}}>אין נתונים להצגה. התחל בהגדרת האתר</p>
          <button className="btn btn-primary" onClick={() => navigate(`/sites/${id}/settings`)}>הגדרות</button>
        </div>
      )}
    </>
  );
}
